import {
  GenericApiService,
  IListApiResponse,
  ISingleApiResponse,
} from "../../api/API_GenericService";
import { POSEndpoints } from "src/constants/apiEndpoints";
import {
  IPrinter,
  ICreatePrinter,
  IUpdatePrinter,
  IPrinterFilters,
  IPrinterPagedResponse,
} from "../../models/POS/Printer.models";

// Clase moderna de servicio compatible con Redux Toolkit
export class POSPrinterService {
  private apiService: GenericApiService<IPrinter>;

  constructor() {
    this.apiService = new GenericApiService<IPrinter>();
  }

  // Obtener todas las impresoras con filtros
  async getAll(filters?: IPrinterFilters): Promise<IPrinter[]> {
    const params: any = {};
    if (filters?.searchParam) params.SearchParam = filters.searchParam;
    if (filters?.terminalId) params.TerminalId = filters.terminalId;
    if (filters?.status !== undefined) params.Status = filters.status;

    const response = await this.apiService.getAll(POSEndpoints.printer, params);
    if (response?.success) {
      return response.data;
    }
    throw new Error(response?.message || "Error al obtener impresoras");
  }

  // Obtener impresoras paginadas
  async getPaged(filters: IPrinterFilters): Promise<IPrinterPagedResponse> {
    const params: any = {};
    if (filters?.searchParam) params.SearchParam = filters.searchParam;
    if (filters?.terminalId) params.TerminalId = filters.terminalId;
    if (filters?.status !== undefined) params.Status = filters.status;
    if (filters?.pageNumber) params.PageNumber = filters.pageNumber;

    const response = await this.apiService.getAll(POSEndpoints.printer, params);
    if (response?.success) {
      return {
        items: response.data,
        totalCount: response.data.length,
      };
    }
    throw new Error(response?.message || "Error al obtener impresoras paginadas");
  }

  // Obtener impresora por ID
  async getById(printerId: string): Promise<IPrinter> {
    const response = await this.apiService.getByIdString(
      POSEndpoints.printer,
      printerId
    );
    if (response?.success) {
      return response.data;
    }
    throw new Error(response?.message || "Error al obtener la impresora");
  }

  // Crear nueva impresora
  async create(printerData: ICreatePrinter): Promise<IPrinter> {
    const response = await this.apiService.create(
      POSEndpoints.printer,
      printerData as any
    );
    if (response?.success) {
      return response.data;
    }
    throw new Error(response?.message || "Error al crear la impresora");
  }

  // Actualizar impresora
  async update(printerData: IUpdatePrinter): Promise<IPrinter> {
    const response = await this.apiService.update(
      POSEndpoints.printer,
      printerData as any
    );
    if (response?.success) {
      return response.data;
    }
    throw new Error(response?.message || "Error al actualizar la impresora");
  }

  // Eliminar impresora (soft delete)
  async delete(printerId: string): Promise<void> {
    const printer = await this.getById(printerId);
    const toUpdated = { ...printer, status: false };
    const response = await this.apiService.update(
      POSEndpoints.printer,
      toUpdated
    );
    if (!response?.success) {
      throw new Error(response?.message || "Error al eliminar la impresora");
    }
  }

  // Establecer impresora por defecto
  async setDefault(printerId: string): Promise<IPrinter> {
    const response = await this.apiService.update(
      `${POSEndpoints.printer}/${printerId}/set-default`,
      {} as any
    );
    if (response?.success) {
      return response.data;
    }
    throw new Error(
      response?.message || "Error al establecer impresora por defecto"
    );
  }

  // Obtener impresora por defecto
  async getDefault(): Promise<IPrinter> {
    const response = await this.apiService.getAll(
      `${POSEndpoints.printer}/default`
    );
    if (response?.success && response.data.length > 0) {
      return response.data[0];
    }
    throw new Error(
      response?.message || "No se encontró impresora por defecto"
    );
  }

  // Obtener impresoras por terminal
  async getByTerminal(terminalId: number): Promise<IPrinter[]> {
    const response = await this.apiService.getAll(
      `${POSEndpoints.printer}/terminal/${terminalId}`
    );
    if (response?.success) {
      return response.data;
    }
    throw new Error(
      response?.message || "Error al obtener impresoras del terminal"
    );
  }

  // Activar impresora
  async activate(printerId: string): Promise<IPrinter> {
    const printer = await this.getById(printerId);
    const toUpdated = { ...printer, status: true };
    const response = await this.apiService.update(
      POSEndpoints.printer,
      toUpdated
    );
    if (response?.success) {
      return response.data;
    }
    throw new Error(response?.message || "Error al activar la impresora");
  }
}

// ============================================================================
// FUNCIONES LEGACY (Para retrocompatibilidad con código antiguo)
// ============================================================================

const ApiService = new GenericApiService<IPrinter>();

const GetAllPrinterList = (dispatch: any, pagination?: any) => {
  return new Promise<IListApiResponse<IPrinter>>(async (resolve, reject) => {
    try {
      const response = await ApiService.getAll(
        POSEndpoints.printer,
        pagination?.params
      );
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const GetPrinterById = (dispatch: any, id: string) => {
  return new Promise<ISingleApiResponse<IPrinter>>(async (resolve, reject) => {
    try {
      const response = await ApiService.getByIdString(POSEndpoints.printer, id);
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const AddNewPrinter = (dispatch: any, newItem: IPrinter) => {
  return new Promise<ISingleApiResponse<IPrinter>>(async (resolve, reject) => {
    try {
      const response = await ApiService.create(POSEndpoints.printer, newItem);
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const UpdatePrinterOnList = (dispatch: any, newItem: IPrinter) => {
  return new Promise<ISingleApiResponse<IPrinter>>(async (resolve, reject) => {
    try {
      const response = await ApiService.update(POSEndpoints.printer, newItem);
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const DeletePrinter = (dispatch: any, newItem: IPrinter) => {
  return new Promise<ISingleApiResponse<IPrinter>>(async (resolve, reject) => {
    try {
      const toUpdated = { ...newItem };
      toUpdated.status = false;
      const response = await ApiService.update(
        POSEndpoints.printer,
        toUpdated
      );
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const SetDefaultPrinter = (dispatch: any, printer: IPrinter) => {
  return new Promise<ISingleApiResponse<IPrinter>>(async (resolve, reject) => {
    try {
      const response = await ApiService.update(
        `${POSEndpoints.printer}/${printer.poS_PrinterId}/set-default`,
        printer
      );
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const GetDefaultPrinter = (dispatch: any) => {
  return new Promise<ISingleApiResponse<IPrinter>>(async (resolve, reject) => {
    try {
      const response = await ApiService.getAll(
        `${POSEndpoints.printer}/default`
      );
      if (response?.success && response.data.length > 0) {
        // Convertir IListApiResponse a ISingleApiResponse
        const singleResponse: ISingleApiResponse<IPrinter> = {
          ...response,
          data: response.data[0],
        };
        resolve(singleResponse);
      } else {
        reject(new Error("No se encontró impresora por defecto"));
      }
    } catch (exception) {
      reject(exception);
    }
  });
};

const GetPrintersByTerminal = (dispatch: any, terminalId: number) => {
  return new Promise<IListApiResponse<IPrinter>>(async (resolve, reject) => {
    try {
      const response = await ApiService.getAll(
        `${POSEndpoints.printer}/terminal/${terminalId}`
      );
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const GetPrintersWithFilters = (
  dispatch: any,
  filters?: {
    searchParam?: string;
    terminalId?: number;
    status?: boolean;
  }
) => {
  return new Promise<IListApiResponse<IPrinter>>(async (resolve, reject) => {
    try {
      const params: any = {};
      if (filters?.searchParam) params.SearchParam = filters.searchParam;
      if (filters?.terminalId) params.TerminalId = filters.terminalId;
      if (filters?.status !== undefined) params.Status = filters.status;

      const response = await ApiService.getAll(POSEndpoints.printer, params);
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const ActivatePrinter = (dispatch: any, printerId: string) => {
  return new Promise<ISingleApiResponse<IPrinter>>(async (resolve, reject) => {
    try {
      const getResponse = await ApiService.getByIdString(
        POSEndpoints.printer,
        printerId
      );
      if (!getResponse.success) {
        reject(new Error("No se pudo obtener la impresora"));
        return;
      }

      const toUpdated = { ...getResponse.data, status: true };
      const response = await ApiService.update(POSEndpoints.printer, toUpdated);

      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

export {
  GetPrinterById,
  GetAllPrinterList,
  AddNewPrinter,
  UpdatePrinterOnList,
  DeletePrinter,
  SetDefaultPrinter,
  GetDefaultPrinter,
  GetPrintersByTerminal,
  GetPrintersWithFilters,
  ActivatePrinter,
};